import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleGenAI, LiveServerMessage, Modality, StartSensitivity, EndSensitivity } from "@google/genai";
import { AppConfig, SCENARIO_LABELS, SCENARIO_LIVE_VOICE_NAME } from "./types";
import { createBlob, decode, decodeAudioData } from "../../../tutor/audioUtils";
import { sendLiveOpeningTextOrFallback } from "../../../tutor/genaiLiveHelpers";
import { serviceUrls } from "../../../config/serviceUrls";
import { Mic, MicOff, PhoneOff } from "lucide-react";
import { useFeatureAccess } from "../../../contexts/FeatureAccessContext";
import { handleSchoolFeatureDisabledIfPresent } from "../../../utils/schoolFeatureDisabledStudent";
import {
  AuthSessionExpiredHandledError,
  handleTutorSessionTokenAuthExpiredIfPresent,
  isAuthSessionExpiredHandledError,
} from "../../../utils/tutorSessionTokenAuth";

interface LiveSessionProps {
  config: AppConfig;
  onEnd: () => void;
}

const SCENARIO_SESSION_TOKEN_ENDPOINT = "/v1/tutor/scenario-session-token";
const INPUT_AUDIO_SAMPLE_RATE = 16000;
/** Mic RMS below this counts as silence for client-side end-of-turn (with `audioStreamEnd`). */
const CLIENT_VAD_SILENCE_RMS = 0.005;
/** Shorter than legacy 800ms so the model can start replying sooner after the learner stops. */
const CLIENT_VAD_SILENCE_MS = 350;

/** Nudge model to speak first; Live otherwise often waits for mic/VAD before the first reply. */
const SCENARIO_SESSION_OPENING_CLIENT_TEXT =
  "SESSION_OPENING: The Tamil scenario live session just started. The learner is here and listening (mic may still be muted). " +
  "Do not wait for the learner to speak first. Immediately speak aloud as the scene character: a short warm greeting in Tamil, " +
  "then exactly one simple question that fits the scenario and difficulty in your CONFIG. " +
  "Follow your system instructions: Tamil only for spoken output, 1–3 short sentences total for this opening.";

function getTutorHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token") || "";
  const tenantId = localStorage.getItem("X_TENANT_ID") || "";
  const h: Record<string, string> = { Accept: "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  if (tenantId) h["x-tenant-id"] = tenantId;
  return h;
}

const LiveSession: React.FC<LiveSessionProps> = ({ config, onEnd }) => {
  const navigate = useNavigate();
  const { refetch: refetchFeatureAccess } = useFeatureAccess();
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [userIsSpeaking, setUserIsSpeaking] = useState(false);
  const [isCharacterSpeaking, setIsCharacterSpeaking] = useState(false);

  const TOTAL_TIME = 120;
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasTimerStartedRef = useRef(false);

  const nextStartTimeRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const inputSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const sessionRef = useRef<any>(null);
  /** Resolved Live session for mic chunks — avoids queueing `.then()` on every audio frame. */
  const liveSessionRef = useRef<any>(null);

  const isMutedRef = useRef(isMuted);
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const isCharacterSpeakingRef = useRef(false);
  useEffect(() => {
    isCharacterSpeakingRef.current = isCharacterSpeaking;
  }, [isCharacterSpeaking]);

  const clientSilenceMsRef = useRef(0);
  const clientVadEndSentRef = useRef(false);
  /** Bumped on each effect cleanup so in-flight connect/opening from a previous mount is ignored. */
  const sessionConnectGenRef = useRef(0);

  const { bg: characterBackgroundImage, avatar: characterAvatar, label: characterRoleLabel } =
    useMemo(() => {
      switch (config.scenario) {
        case "fruit_shop":
          return {
            bg: "/scenes/fruit-shop-bg.jpg",
            avatar: "/avatars/shopkeeper.png",
            label: "பழக் கடை உரிமையாளர்",
          };
        case "library":
          return {
            bg: "/scenes/library-bg.jpg",
            avatar: "/avatars/librarian.png",
            label: "நூலக நிர்வாகி",
          };
        case "visiting_friend":
          return {
            bg: "/scenes/friend-home-bg.jpg",
            avatar: "/avatars/friend.png",
            label: "நண்பர்",
          };
        case "doctor_clinic":
          return {
            bg: "/scenes/clinic-bg.jpg",
            avatar: "/avatars/doctor.png",
            label: "டாக்டர்",
          };
        default:
          return {
            bg: "/scenes/default-bg.jpg",
            avatar: "/avatars/shopkeeper.png",
            label: "முக்கிய பாத்திரம்",
          };
      }
    }, [config.scenario]);

  useEffect(() => {
    if (!isConnected) return;

    if (!isMuted && !hasTimerStartedRef.current) {
      hasTimerStartedRef.current = true;

      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            onEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [isMuted, isConnected, onEnd]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  async function fetchEphemeralToken(): Promise<string | null> {
    const url = `${serviceUrls.gateway}${SCENARIO_SESSION_TOKEN_ENDPOINT}`;
    const res = await fetch(url, {
      method: "GET",
      headers: getTutorHeaders(),
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      if (
        await handleSchoolFeatureDisabledIfPresent({
          status: res.status,
          bodyText: txt,
          navigate,
          refetchFeatureAccess,
          fallbackFeatureKey: "scenario_based_convo",
        })
      ) {
        return null;
      }
      if (
        handleTutorSessionTokenAuthExpiredIfPresent({
          status: res.status,
          bodyText: txt,
          navigate,
        })
      ) {
        throw new AuthSessionExpiredHandledError();
      }
      throw new Error(`Token fetch failed: ${res.status} ${txt}`);
    }

    const data = await res.json();
    const token = data?.token;
    if (!token || typeof token !== "string") throw new Error("Invalid token from backend.");
    return token;
  }

  useEffect(() => {
    let cleanup = false;
    const connectGen = ++sessionConnectGenRef.current;

    const startSession = async () => {
      try {
        setError(null);

        const ephToken = await fetchEphemeralToken();
        if (ephToken === null) return;
        if (connectGen !== sessionConnectGenRef.current) return;

        const ai = new GoogleGenAI({
          apiKey: ephToken,
          httpOptions: { apiVersion: "v1alpha" },
        });

        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        const outputCtx = new AudioContextClass({ sampleRate: 24000 });
        const inputCtx = new AudioContextClass({ sampleRate: 16000 });

        audioContextRef.current = outputCtx;
        inputAudioContextRef.current = inputCtx;

        try {
          await inputCtx.resume();
          await outputCtx.resume();
        } catch {
          /* browsers may keep suspended until gesture — onopen will try again */
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (connectGen !== sessionConnectGenRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        const liveVoiceName = SCENARIO_LIVE_VOICE_NAME[config.scenario];

        const systemInstruction = `
You are a Tamil Nadu Tamil speaking conversation partner for real-life scenarios, used in speech-to-speech mode.

========================
CRITICAL LANGUAGE RULES
========================

- Think and speak only in Tamil.
- Always reply only in Tamil script.
- Speak in natural Tamil Nadu spoken Tamil.
- Keep replies short: 1–3 sentences.
- Tanglish input is allowed from the learner.
- Do NOT over-correct the learner.
- Do NOT behave like a grammar teacher.
- Stay natural, casual, and conversational.
- Sound like a real human, not an AI assistant.

========================
HUMAN CONVERSATION STYLE
========================

Talk like a real Tamil Nadu person.

The conversation should feel:

- warm
- casual
- friendly
- relaxed
- natural
- human

Use natural spoken reactions sometimes:

- ஆமா
- சரிங்க
- ஹா ஹா
- அப்படியா
- ஓஹோ
- பரவாயில்ல
- சரி பாக்கலாம்
- கண்டிப்பா
- இதோ தர்றேன்
- ஒரு நிமிஷம்
- நல்லா இருக்கு
- fresh ஆ இருக்கு
- ரொம்ப நல்லா இருக்கு

Use soft spoken endings naturally:

- இல்லங்க
- இருக்கு
- தர்றேன்
- பாத்துக்கலாம்
- சொல்லுங்க
- வாங்கங்க

Examples:

Instead of:
- "உங்களுக்கு என்ன வேண்டும்?"

Use:
- "என்ன வேணும் சொல்லுங்க?"
- "எதை வாங்கணும்?"
- "இன்னும் ஏதாவது வேணுமா?"

Instead of:
- "இந்த பொருள் கிடைக்காது"

Use:
- "அது இங்க இல்லங்க"
- "அது விற்கமாட்டோம்"
- "அது இங்க கிடையாது"

Do NOT sound:

- robotic
- overly polite
- textbook Tamil
- formal written Tamil

========================
TAMIL NADU SPOKEN STYLE
========================

Use natural Tamil Nadu spoken Tamil:

- ஆமா
- இல்ல
- சரி
- வாங்க
- பாருங்க
- சொல்லுங்க
- கேளுங்க
- வேணும்
- எவ்வளவு
- எப்படி இருக்கு
- இன்னும் ஏதாவது வேணுமா
- போறேன்
- வாங்குறேன்
- சொல்றேன்

Avoid:

Eelam / Sri Lankan Tamil style:

- ஓம்
- வாங்கோ
- பாருங்கோ
- சொல்லுங்கோ

Avoid:

- formal written Tamil
- Hindi words
- English replies

========================
ROLE
========================

- Human user = learner (${config.learnerRole})

- You act as the main character inside the scenario.

- There is NO teacher persona.

- Speak naturally as the scenario character.

========================
CONFIG
========================

{
  "scenario": "${config.scenario}",
  "learner_role": "${config.learnerRole}",
  "difficulty": "${config.difficulty}"
}

========================
FLOW
========================

1) You will receive SESSION_OPENING.

2) Start immediately as the scenario character.

3) Speak first naturally.

4) Ask only ONE simple follow-up question each turn.

5) Keep replies short and human.

========================
TANGLISH INPUT RULE
========================

Tanglish typing is allowed.

Examples:

- "enaku apple venum"
- "1 kg mango venum"
- "doctor appointment venum"

Do NOT correct if the meaning is clear.

Understand naturally and continue in Tamil script.

Example:

Learner:
"enaku apple venum"

Reply:
"சரி, ஆப்பிள் தர்றேன். எவ்வளவு வேணும்?"

Correct ONLY if:

- meaning is unclear
- another language is used
- wrong item category is used
- wrong measurement/unit is used
- sentence becomes confusing

========================
CORRECTION RULE
========================

Correct ONLY when:

- learner speaks fully in English
- learner uses another language
- wrong item category is used
- wrong measurement/unit is used
- sentence meaning becomes unclear

Do NOT correct:

- proper Tamil script
- casual Tamil
- spoken Tamil Nadu Tamil
- clear Tanglish
- short Tamil replies

Do NOT unnecessarily rewrite Tamil sentences.

If learner already speaks correctly in Tamil,
continue naturally without correction.

========================
WHEN CORRECTING
========================

Use only ONE short correction line.

Examples:

- "சின்ன திருத்தம்:"
- "தமிழ்ல இப்படிச் சொல்லலாம்:"

Then continue naturally.

Do NOT:

- teach grammar
- give long explanation
- behave like teacher

Maximum:
- 1 correction per turn

========================
ENGLISH INPUT RULE
========================

If learner speaks fully in English,
briefly correct into Tamil and continue naturally.

Example:

Learner:
"I want apple"

Reply:
"சின்ன திருத்தம்: எனக்கு ஆப்பிள் வேணும். எவ்வளவு வேணும்?"

========================
TAMIL INPUT RULE
========================

If learner already speaks correctly in Tamil script,
DO NOT correct.

Example:

Learner:
"எனக்கு ஆப்பிள் வேணும்"

Reply:
"சரி, எத்தனை கிலோ வேணும்?"

Do NOT say:
"சின்ன திருத்தம்"

========================
STRICT TOPIC CONTROL
========================

Stay ONLY inside the selected scenario.

If scenario = fruit_shop:

- Sell only fruits.
- Do NOT sell vegetables.
- Do NOT sell milk.
- Do NOT sell grocery items.
- Do NOT sell medicine.
- Do NOT sell books.

If learner asks unavailable item:

- Say it is not available here.
- Do NOT force another sentence.
- Continue naturally.

Example:

Learner:
"எனக்கு வெங்காயம் வேணும்"

Reply:
"அது இங்க இல்லங்க. பழம் ஏதாவது வேணுமா?"

Example:

Learner:
"enaku carrot venum"

Reply:
"கேரட் இங்க விற்கமாட்டோம். பழம் வாங்கறீங்களா?"

========================
MEASURE / UNIT CORRECTION
========================

Correct wrong measurement politely.

Examples:

- milk → litre
- juice → litre / glass
- tablets → count
- fruits → kg / count
- books → count
- appointment → time/date

Example:

Learner:
"ஒரு கிலோ பால் வேணும்"

Reply:
"சின்ன திருத்தம்: பால் லிட்டர்ல தான் கேக்கணும். ஆனா இங்க பால் விற்கமாட்டோம்."

Example:

Learner:
"1 kg milk venum"

Reply:
"சின்ன திருத்தம்: பால் லிட்டர்ல தான் கேக்கணும். ஆனா இங்க பால் விற்கமாட்டோம்."

========================
REGIONAL WORD GUIDANCE
========================

If learner asks regional meanings,
explain briefly.

Example:

Learner:
"onion means ulli ah?"

Reply:
"சில இடங்களில் உள்ளின்னு சொல்வாங்க, தமிழ்நாடு வழக்கில் வெங்காயம் என்பாங்க. ஆனா இங்க வெங்காயம் விற்கமாட்டோம்."

Do not over-explain.

========================
SCENARIO STYLE
========================

fruit_shop:

- Speak like a friendly fruit shop owner.

Available:

- ஆப்பிள்
- வாழைப்பழம்
- மாம்பழம்
- திராட்சை
- ஆரஞ்சு
- மாதுளை
- கொய்யா

Not available:

- காய்கறி
- பால்
- அரிசி
- மருந்து
- புத்தகம்

vegetable_shop:

- Speak like a casual vegetable shop owner.

Available:

- வெங்காயம்
- தக்காளி
- உருளைக்கிழங்கு
- கத்திரிக்காய்
- கேரட்
- பீன்ஸ்

Not available:

- பழம்
- பால்
- மருந்து
- புத்தகம்

library:

- Speak politely like a librarian.
- Books only.
- Do not sell food or medicine.

doctor_clinic:

- Speak calmly and warmly like a doctor.
- Discuss:
  - appointment
  - symptoms
  - medicine guidance

Do not discuss groceries or shopping.

visiting_friend:

- Speak casually like a close friend.
- Friendly conversation only.

========================
CLOSING
========================

After a few turns:

- End politely.
- Give one useful Tamil phrase naturally.
- Ask one final simple question.

========================
FINAL CHECK
========================

Before every reply:

- Tamil script only
- Tamil Nadu spoken style
- Warm human tone
- Casual natural speaking
- No robotic replies
- No over-correction
- Correct only when necessary
- Do not correct proper Tamil
- Tanglish allowed
- Stay inside scenario
- Keep replies short
- Sound like a real Tamil Nadu person
`;


        const sessionPromise = ai.live.connect({
          model: "gemini-2.5-flash-native-audio-preview-09-2025",
          config: {
            systemInstruction,
            responseModalities: [Modality.AUDIO],
            // Native audio: thinking on by default — noticeably delays first spoken token.
            thinkingConfig: { thinkingBudget: 0 },
            speechConfig: {
              voiceConfig: { prebuiltVoiceConfig: { voiceName: liveVoiceName } },
            },
            realtimeInputConfig: {
              automaticActivityDetection: {
                startOfSpeechSensitivity: StartSensitivity.START_SENSITIVITY_HIGH,
                // Lower = finalize user turn sooner → model starts sooner (small false-end risk).
                endOfSpeechSensitivity: EndSensitivity.END_SENSITIVITY_LOW,
                prefixPaddingMs: 20,
                silenceDurationMs: 400,
              },
            },
            inputAudioTranscription: {},
            outputAudioTranscription: {},
          },
          callbacks: {
            onopen: () => {
              if (connectGen !== sessionConnectGenRef.current) return;
              setIsConnected(true);
              clientSilenceMsRef.current = 0;
              clientVadEndSentRef.current = false;

              void sessionPromise.then((session) => {
                if (cleanup || connectGen !== sessionConnectGenRef.current) return;
                liveSessionRef.current = session;
              });

              const source = inputCtx.createMediaStreamSource(stream);
              // Smaller buffer than 4096 → mic audio reaches the model in smaller steps (lower pipeline latency).
              const scriptProcessor = inputCtx.createScriptProcessor(2048, 1, 1);

              const sendStreamEnd = () => {
                const session = liveSessionRef.current;
                const p = session
                  ? Promise.resolve(session)
                  : sessionPromise;
                p.then((s) => s.sendRealtimeInput({ audioStreamEnd: true })).catch(() => { });
              };

              scriptProcessor.onaudioprocess = (e) => {
                if (cleanup || connectGen !== sessionConnectGenRef.current) return;

                if (isMutedRef.current) {
                  setUserIsSpeaking(false);
                  clientSilenceMsRef.current = 0;
                  clientVadEndSentRef.current = false;
                  return;
                }

                if (isCharacterSpeakingRef.current) {
                  setUserIsSpeaking(false);
                  clientSilenceMsRef.current = 0;
                  clientVadEndSentRef.current = false;
                  return;
                }

                const inputData = e.inputBuffer.getChannelData(0);

                let sum = 0;
                for (let i = 0; i < inputData.length; i++) sum += inputData[i] * inputData[i];
                const rms = Math.sqrt(sum / inputData.length);
                setUserIsSpeaking(rms > 0.02);

                const pcmBlob = createBlob(inputData);
                const session = liveSessionRef.current;
                if (session) {
                  session.sendRealtimeInput({ media: pcmBlob }).catch(() => { });
                } else {
                  sessionPromise
                    .then((s) => s.sendRealtimeInput({ media: pcmBlob }))
                    .catch(() => { });
                }

                const bufferMs = (inputData.length / INPUT_AUDIO_SAMPLE_RATE) * 1000;
                if (rms < CLIENT_VAD_SILENCE_RMS) {
                  clientSilenceMsRef.current += bufferMs;
                  if (
                    clientSilenceMsRef.current >= CLIENT_VAD_SILENCE_MS &&
                    !clientVadEndSentRef.current
                  ) {
                    clientVadEndSentRef.current = true;
                    sendStreamEnd();
                  }
                } else {
                  clientSilenceMsRef.current = 0;
                  clientVadEndSentRef.current = false;
                }
              };

              source.connect(scriptProcessor);
              scriptProcessor.connect(inputCtx.destination);

              inputSourceRef.current = source;
              processorRef.current = scriptProcessor;
            },

            onmessage: async (message: LiveServerMessage) => {
              if (cleanup || connectGen !== sessionConnectGenRef.current) return;

              const base64Audio = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
              if (base64Audio) {
                setIsCharacterSpeaking(true);

                try {
                  const audioCtx = audioContextRef.current;
                  if (!audioCtx) return;

                  nextStartTimeRef.current = Math.max(nextStartTimeRef.current, audioCtx.currentTime);

                  const audioBuffer = await decodeAudioData(decode(base64Audio), audioCtx, 24000, 1);

                  const source = audioCtx.createBufferSource();
                  source.buffer = audioBuffer;

                  const gainNode = audioCtx.createGain();
                  gainNode.gain.value = 1.0;

                  source.connect(gainNode);
                  gainNode.connect(audioCtx.destination);

                  source.addEventListener("ended", () => {
                    sourcesRef.current.delete(source);
                    if (sourcesRef.current.size === 0) setIsCharacterSpeaking(false);
                  });

                  source.start(nextStartTimeRef.current);
                  nextStartTimeRef.current += audioBuffer.duration;
                  sourcesRef.current.add(source);
                } catch (err) {
                  console.error("Error decoding audio", err);
                }
              }

              if (message.serverContent?.turnComplete) {
                if (sourcesRef.current.size === 0) setIsCharacterSpeaking(false);
              }
            },

            onclose: () => {
              setIsConnected(false);
            },

            onerror: (err) => {
              console.error("Gemini Live API Error:", err);
              setError("Connection error. Please try again.");
              setIsConnected(false);
            },
          },
        });

        sessionRef.current = sessionPromise;

        // Fire opening as soon as the session object exists — not only from `onopen`
        // (ordering differs by SDK/build; nesting under onopen can miss the first turn).
        void (async () => {
          try {
            const session = await sessionPromise;
            if (cleanup || connectGen !== sessionConnectGenRef.current) return;

            liveSessionRef.current = session;

            try {
              await outputCtx.resume();
              await inputCtx.resume();
            } catch {
              /* non-fatal */
            }

            if (cleanup || connectGen !== sessionConnectGenRef.current) return;

            await new Promise<void>((r) => {
              window.setTimeout(r, 80);
            });

            if (cleanup || connectGen !== sessionConnectGenRef.current) return;

            await sendLiveOpeningTextOrFallback(session, SCENARIO_SESSION_OPENING_CLIENT_TEXT);
          } catch (e) {
            if (import.meta.env.DEV && connectGen === sessionConnectGenRef.current) {
              console.warn("[LiveSession] opening turn failed", e);
            }
          }
        })();
      } catch (err: any) {
        if (isAuthSessionExpiredHandledError(err)) {
          try {
            streamRef.current?.getTracks().forEach((track) => track.stop());
          } catch { }
          streamRef.current = null;
          try {
            inputSourceRef.current?.disconnect();
            processorRef.current?.disconnect();
          } catch { }
          try {
            void audioContextRef.current?.close();
            void inputAudioContextRef.current?.close();
          } catch { }
          audioContextRef.current = null;
          inputAudioContextRef.current = null;
          setIsConnected(false);
          setError(null);
          return;
        }
        console.error("Setup failed", err);
        setError(err?.message || "Failed to start session");
        setIsConnected(false);
      }
    };

    startSession();

    return () => {
      cleanup = true;
      sessionConnectGenRef.current += 1;
      liveSessionRef.current = null;

      if (sessionRef.current) {
        sessionRef.current.then((session: any) => session.close()).catch(() => { });
      }

      try {
        streamRef.current?.getTracks().forEach((track) => track.stop());
      } catch { }
      try {
        inputSourceRef.current?.disconnect();
        processorRef.current?.disconnect();
      } catch { }
      try {
        audioContextRef.current?.close();
        inputAudioContextRef.current?.close();
      } catch { }
    };
  }, [config.scenario, config.learnerRole, config.difficulty]);

  const toggleMute = () => setIsMuted((prev) => !prev);

  const endNow = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onEnd();
  };

  return (
    <div className="flex flex-col h-screen text-white overflow-hidden relative">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      <div className="absolute inset-0 bg-black/30" />

      <div className="relative z-10 px-4 py-3 md:px-5 md:py-4 flex justify-between items-center bg-slate-950/80 backdrop-blur-md border-b border-white/10">
        <div className="flex flex-col items-start">
          <span className="text-[11px] text-slate-400">Time Left</span>
          <span className="text-base font-bold text-amber-400">{timeLeft}s</span>
        </div>

        <div className="text-center">
          <h2 className="text-base md:text-lg font-bold text-white">
            {SCENARIO_LABELS[config.scenario]}
          </h2>
          <p className="text-[11px] md:text-xs text-slate-300">
            Role: {config.learnerRole} • {config.difficulty.toUpperCase()}
          </p>
        </div>

        <div
          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-2 ${isConnected ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"
            }`}
        >
          <div
            className={`w-2 h-2 rounded-full ${isConnected ? "bg-green-300 animate-pulse" : "bg-red-300"
              }`}
          />
          {isConnected ? "LIVE" : "CONNECTING..."}
        </div>
      </div>

      <div className="relative z-10 flex-1 p-3 md:p-5 overflow-hidden">
        <div className="h-full max-w-3xl mx-auto flex items-center justify-center">
          <div className="w-full">
            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-sm rounded-2xl px-4 py-2 border border-amber-400/30 mb-3">
              <h3 className="text-sm font-bold text-amber-200 text-center">Scene Character</h3>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-2 border-amber-400/30 bg-slate-900/60">
              <div
                className="absolute inset-0 bg-cover bg-center opacity-50"
                style={{ backgroundImage: `url(${characterBackgroundImage})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-br from-amber-900/40 via-slate-950/60 to-slate-950/80" />

              <div className="relative flex flex-col items-center justify-center p-6 md:p-8 min-h-[420px]">
                <div
                  className={`absolute inset-0 transition-all duration-500 ${isCharacterSpeaking ? "opacity-100" : "opacity-0"
                    }`}
                >
                  <div className="absolute inset-0 bg-amber-400/10" />
                </div>

                <div className="relative">
                  <div
                    className={`absolute inset-0 rounded-full blur-3xl transition-all duration-500 ${isCharacterSpeaking ? "bg-amber-400/60 scale-150" : "bg-transparent"
                      }`}
                  />

                  <div
                    className={`relative w-44 h-44 md:w-56 md:h-56 rounded-full flex items-center justify-center border-4 shadow-2xl overflow-hidden transition-all duration-300 ${isCharacterSpeaking
                        ? "border-amber-300 bg-amber-900/40 scale-110"
                        : "border-amber-600/50 bg-slate-900/80"
                      }`}
                  >
                    <img
                      src={characterAvatar}
                      alt="Character"
                      className={`w-full h-full object-cover transition-transform duration-300 ${isCharacterSpeaking ? "scale-105" : "scale-100"
                        }`}
                    />
                  </div>

                  <div className="mt-4 text-center text-sm text-amber-200 font-semibold drop-shadow px-2">
                    {characterRoleLabel}
                  </div>

                  <div className="mt-3 text-center text-xs text-slate-300">
                    {isMuted ? "Mic muted — unmute to talk" : "Mic on — speak now"}
                  </div>

                  <div className="mt-4 flex items-center justify-center gap-2">
                    <span className="text-[11px] text-slate-400">You:</span>
                    <div className="h-4 flex gap-1 items-end">
                      {userIsSpeaking ? (
                        [...Array(7)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1.5 rounded-full bg-emerald-400 animate-bounce"
                            style={{
                              height: `${Math.random() * 12 + 6}px`,
                              animationDelay: `${i * 0.07}s`,
                            }}
                          />
                        ))
                      ) : (
                        <span className="text-[11px] text-slate-500">listening</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 text-center text-[11px] text-slate-400">
              பேசும்போது தவறு இருந்தா, இந்த Scene Character தானே மெதுவா சரி சொல்லி உதவி பண்ணும்.
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-red-500/90 text-white px-4 py-2 rounded-full shadow-lg z-50 text-sm">
          {error}
        </div>
      )}

      <div className="relative z-10 p-3 md:p-4 flex justify-center gap-3 bg-slate-950/90 backdrop-blur-lg border-t border-white/10">
        <button
          onClick={toggleMute}
          className={`p-2.5 md:p-3 rounded-full transition-all shadow-lg ${isMuted
              ? "bg-red-500/25 text-red-200 hover:bg-red-500/35 border border-red-400/60"
              : "bg-slate-800/70 hover:bg-slate-700 text-white border border-slate-600"
            }`}
          aria-label={isMuted ? "Unmute" : "Mute"}
          title={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? (
            <MicOff className="w-4 h-4 md:w-5 md:h-5" />
          ) : (
            <Mic className="w-4 h-4 md:w-5 md:h-5" />
          )}
        </button>

        <button
          onClick={endNow}
          className="px-4 md:px-5 py-2.5 md:py-3 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold flex items-center gap-2 shadow-lg text-sm"
        >
          <PhoneOff className="w-4 h-4" />
          End
        </button>
      </div>
    </div>
  );
};

export default LiveSession;
